import { env } from '@/lib/env';
import { GmailError, type GmailErrorCode } from '@/lib/gmail/errors';

export const GMAIL_COMPOSE_SCOPE =
  'https://www.googleapis.com/auth/gmail.compose';
const GIS_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const EXPIRY_MARGIN_MS = 60_000;

export interface GisTokenResponse {
  access_token?: string;
  expires_in?: number | string;
  scope?: string;
  error?: string;
}

export interface GisPopupError {
  type: string;
}

export interface GisTokenClientConfig {
  client_id: string;
  scope: string;
  callback: (response: GisTokenResponse) => void;
  error_callback: (error: GisPopupError) => void;
}

export interface GisOAuth2 {
  initTokenClient: (config: GisTokenClientConfig) => {
    requestAccessToken: () => void;
  };
  hasGrantedAllScopes: (
    response: GisTokenResponse,
    ...scopes: string[]
  ) => boolean;
}

declare global {
  interface Window {
    google?: { accounts?: { oauth2?: GisOAuth2 } };
  }
}

export type GmailTokenClientState =
  | 'idle'
  | 'unavailable'
  | 'loading'
  | 'ready'
  | 'failed';

interface HeldToken {
  token: string;
  expiresAt: number;
}

interface PendingRequest {
  promise: Promise<string>;
  resolve: (token: string) => void;
  reject: (error: GmailError) => void;
}

let state: GmailTokenClientState = 'idle';
let loading: Promise<void> | null = null;
let requestAccessToken: (() => void) | null = null;
let held: HeldToken | null = null;
let pending: PendingRequest | null = null;
const listeners = new Set<() => void>();

const setState = (next: GmailTokenClientState): void => {
  state = next;
  for (const listener of listeners) listener();
};

export const gmailTokenClientState = (): GmailTokenClientState => state;

export const subscribeGmailTokenClient = (
  listener: () => void,
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const isFresh = (token: HeldToken | null): token is HeldToken =>
  token !== null && token.expiresAt - EXPIRY_MARGIN_MS > Date.now();

const takePending = (): PendingRequest | null => {
  const current = pending;
  pending = null;
  return current;
};

const tokenErrorCode = (error: string): GmailErrorCode => {
  if (error === 'access_denied') return 'scope_denied';
  if (error === 'admin_policy_enforced' || error === 'org_internal') {
    return 'admin_blocked';
  }
  return 'upstream';
};

const grantedToken = (
  oauth2: GisOAuth2,
  response: GisTokenResponse,
): HeldToken | GmailErrorCode => {
  if (response.error) return tokenErrorCode(response.error);
  if (
    !response.access_token ||
    !oauth2.hasGrantedAllScopes(response, GMAIL_COMPOSE_SCOPE)
  ) {
    return 'scope_denied';
  }
  const expiresAt = Date.now() + Number(response.expires_in) * 1000;
  return { token: response.access_token, expiresAt };
};

const onToken = (oauth2: GisOAuth2, response: GisTokenResponse): void => {
  const request = takePending();
  if (!request) return;
  const granted = grantedToken(oauth2, response);
  if (typeof granted === 'string') {
    request.reject(new GmailError(granted));
    return;
  }
  if (!isFresh(granted)) {
    request.reject(new GmailError('upstream'));
    return;
  }
  held = granted;
  request.resolve(granted.token);
};

const onPopupError = (error: GisPopupError): void => {
  const closed =
    error.type === 'popup_closed' || error.type === 'popup_failed_to_open';
  takePending()?.reject(new GmailError(closed ? 'popup_closed' : 'upstream'));
};

const gisLoaded = (): Promise<GisOAuth2> => {
  const present = window.google?.accounts?.oauth2;
  if (present) return Promise.resolve(present);
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = GIS_SCRIPT_URL;
    script.addEventListener('load', () => {
      const oauth2 = window.google?.accounts?.oauth2;
      if (oauth2) resolve(oauth2);
      else reject(new Error('Google Identity Services did not initialise'));
    });
    script.addEventListener('error', () => {
      reject(new Error('Google Identity Services failed to load'));
    });
    document.head.append(script);
  });
};

const initialise = (oauth2: GisOAuth2, clientId: string): void => {
  const tokenClient = oauth2.initTokenClient({
    client_id: clientId,
    scope: GMAIL_COMPOSE_SCOPE,
    callback: (response) => onToken(oauth2, response),
    error_callback: onPopupError,
  });
  requestAccessToken = () => tokenClient.requestAccessToken();
  setState('ready');
};

// On first render of a card that can save a draft: a popup opened after an await is blocked.
export const loadGmailTokenClient = (
  clientId: string | undefined = env.VITE_GMAIL_CLIENT_ID,
): Promise<void> => {
  if (loading) return loading;
  if (!clientId) {
    setState('unavailable');
    loading = Promise.resolve();
    return loading;
  }
  setState('loading');
  loading = gisLoaded().then(
    (oauth2) => initialise(oauth2, clientId),
    () => setState('failed'),
  );
  return loading;
};

// Call synchronously inside the click handler: nothing before requestAccessToken may await.
export const requestGmailToken = (): Promise<string> => {
  if (isFresh(held)) return Promise.resolve(held.token);
  if (pending) return pending.promise;
  if (!requestAccessToken) return Promise.reject(new GmailError('upstream'));
  let resolve: (token: string) => void = () => {};
  let reject: (error: GmailError) => void = () => {};
  const promise = new Promise<string>((onResolve, onReject) => {
    resolve = onResolve;
    reject = onReject;
  });
  pending = { promise, resolve, reject };
  requestAccessToken();
  return promise;
};

// Gmail refused this token (401): the next click asks Google again.
export const discardGmailToken = (token: string): void => {
  if (held?.token === token) held = null;
};

export const clearGmailToken = (): void => {
  held = null;
  takePending()?.reject(new GmailError('upstream'));
};
